import { Button } from "@common/components/ui/button"
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@common/components/ui/card"
import { useTrackEvent } from "@common/hooks/use-track-event"
import { cn } from "@common/lib/utils"
import { Link, useRouterState } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { useEffect } from "react"

const NotFound = ({
  contained = false,
  title = "Page not found",
  description = "The item or page you tried to open doesn’t exist or may have been removed. Return to the previous page to continue where you left off.",
}: {
  contained?: boolean
  title?: string
  description?: string
}): React.ReactElement => {
  const trackEvent = useTrackEvent()
  const location = useRouterState({ select: (state) => state.location })

  useEffect(() => {
    trackEvent("404_page_viewed", {
      path: location.pathname,
      referrer: document.referrer || undefined,
      from_trace_id: location.search.from_trace_id,
    })
  }, [location.pathname, location.search.from_trace_id, trackEvent])

  return (
    <div
      className={cn(
        "flex items-center justify-center p-4",
        contained ? "min-h-full" : "min-h-screen",
      )}
    >
      <div className="flex w-full max-w-sm flex-col items-center gap-10">
        {!contained && (
          <img
            src="/assets/images/fleetcraft-logo-indigo.svg"
            alt="Fleetcraft"
            className="h-auto max-h-8 w-auto"
          />
        )}
        <Card className="w-full">
          <CardHeader className="text-center">
            <p className="text-small-medium text-muted-foreground">404</p>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center gap-2">
            <Button onClick={() => window.history.back()}>
              <ArrowLeft />
              Go back
            </Button>
            <Button asChild variant="secondary">
              <Link to="/" preload={false}>
                Go to dashboard
              </Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}

export default NotFound
